import { useCallback, useMemo } from 'react';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { useBasketAvailability } from '../../../bff-api/baskets/useBasketAvailability';
import { useProducts } from '../../../bff-api/products/useProducts';
import { SHIPPING_PROTECTION_PRODUCT_ID } from '../../../constants';
import {
    getBasketInventoryFlag,
    getBasketInventoryFlags,
} from '../../../contexts/utils/inventory-utils';
import { useCustomPreferenceOrDefault } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';
import type { InventoryDetails } from '../../../bff-api/baskets/model/BasketAvailability';
import type { Product } from '../../../bff-api/products/model';
import type { InventoryFlags } from '../../../contexts/utils/types';

interface ProductState {
    flags?: InventoryFlags;
    availability?: InventoryDetails;
    product?: Product;
}

export type GetStateForProduct = (productId: string) => ProductState | undefined;
export type GetFlagsForProduct = (
    productId: string,
    quantity: number,
) => InventoryFlags | undefined;

export type UseBasketProductState = () => {
    productStates: Map<string, ProductState>;
    getStateForProduct: GetStateForProduct;
    getFlagsForProduct: GetFlagsForProduct;
};

export const useBasketProductState: UseBasketProductState = () => {
    const thresholdOnlyXLeft = useCustomPreferenceOrDefault<number>(
        CustomPreference.THRESHOLD_ONLY_X_LEFT_BADGE,
        5,
    );
    const thresholdLimitedQuantity = useCustomPreferenceOrDefault<number>(
        CustomPreference.THRESHOLD_LIMITED_QUANTITY_BADGE,
        10,
    );
    const { data: basket, isError: isBasketError, error: basketError } = useBasket();
    const {
        data: basketAvailability,
        isError: isAvailabilityError,
        error: availabilityError,
    } = useBasketAvailability();

    if (isBasketError || isAvailabilityError) {
        throw basketError ?? availabilityError;
    }

    const productItems = useMemo(() => basket?.productItems ?? [], [basket?.productItems]);

    // Filter out Safonia (shipping protection) - it's a virtual service product that doesn't need product details fetched
    const productIds = productItems
        .map((pi) => pi.productId)
        .filter((id) => id !== SHIPPING_PROTECTION_PRODUCT_ID);
    const { data: products = [] } = useProducts(productIds, { returnOffline: true });

    const productSelection = useMemo(
        () =>
            productItems.map(({ productId, quantity }) => ({
                productId,
                quantity,
            })),
        [productItems],
    );

    const productMap: Map<string, Product> = useMemo(
        () =>
            products?.length
                ? new Map(products.map((product) => [product.id, product]))
                : new Map<string, Product>(),
        [products],
    );

    const inventoryMap: Map<string, InventoryDetails> = useMemo(
        () =>
            basketAvailability?.itemsAvailableToSell
                ? new Map(
                      productItems
                          .filter(
                              ({ productId }) =>
                                  !!basketAvailability.itemsAvailableToSell[productId],
                          )
                          .map((pi) => [
                              pi.productId,
                              basketAvailability.itemsAvailableToSell[pi.productId],
                          ]),
                  )
                : new Map<string, InventoryDetails>(),
        [productItems, basketAvailability?.itemsAvailableToSell],
    );

    const flagMap = useMemo(
        () =>
            getBasketInventoryFlags({
                productSelection,
                inventory: inventoryMap,
                thresholdOnlyXLeft,
                thresholdLimitedQuantity,
            }),
        [productSelection, inventoryMap, thresholdOnlyXLeft, thresholdLimitedQuantity],
    );

    const productStates = useMemo(
        () =>
            new Map<string, ProductState>(
                productItems
                    .map(({ productId }) => {
                        const productFlag = flagMap.get(productId);
                        const productInventory = inventoryMap.get(productId);
                        const product = productMap.get(productId);
                        if (productFlag && productInventory && product) {
                            return [
                                productId,
                                {
                                    flags: productFlag,
                                    availability: productInventory,
                                    product,
                                },
                            ] as [string, ProductState];
                        }
                        return undefined;
                    })
                    .filter((pi): pi is [string, ProductState] => pi !== undefined),
            ),
        [productItems, flagMap, inventoryMap, productMap],
    );

    const getStateForProduct = useCallback(
        (productId: string): ProductState | undefined => {
            const flags = flagMap.get(productId);
            const availability = inventoryMap.get(productId);
            const product = productMap.get(productId);

            if (!flags && !availability && !product) {
                return undefined;
            }

            return {
                flags,
                availability,
                product,
            };
        },
        [flagMap, inventoryMap, productMap],
    );

    const getFlagsForProduct = useCallback(
        (productId: string, quantity: number): InventoryFlags | undefined =>
            getBasketInventoryFlag({
                productId,
                quantity,
                inventory: inventoryMap,
                thresholdOnlyXLeft,
                thresholdLimitedQuantity,
            }),
        [inventoryMap, thresholdOnlyXLeft, thresholdLimitedQuantity],
    );

    return {
        productStates,
        getStateForProduct,
        getFlagsForProduct,
    };
};
